import * as Yup from 'yup';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import {
    PoolButton,
    PoolFormikTextfield,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
} from '@synergy/components-next';
import { PoolMarkdownEditor } from '@synergy/components';

import { PurchaseOrderGenericItem, PurchaseOrderGenericItemType } from '../../../models/purchase-order-generic-item';

interface GenericItemModalProps {
    showGenericItemModal: boolean;
    onModalClose: () => void;
    onSave: (item: PurchaseOrderGenericItem) => void;
    genericItem?: PurchaseOrderGenericItem;
    genericItemType?: PurchaseOrderGenericItemType;
}

export const GenericItemModal = (props: GenericItemModalProps) => {
    const { showGenericItemModal, onModalClose, onSave, genericItem, genericItemType } = props;
    const intl = useIntl();

    const handleSubmit = (values: Partial<PurchaseOrderGenericItem>) => {
        onSave(values as PurchaseOrderGenericItem);
        onModalClose();
    };

    const handleClose = (formik: FormikProps<Partial<PurchaseOrderGenericItem>>) => () => {
        formik.resetForm();
        onModalClose();
    };

    const validationSchema = Yup.object().shape({
        title: Yup.string().required('app.error.required'),
    });

    const initialValues: Partial<PurchaseOrderGenericItem> = genericItem || {
        id: Date.now(),
        title: intl.formatMessage({ id: 'app.purchaseOrders.detail.genericItemDefaultTitle.' + genericItemType }),
        type: genericItemType,
        tax: 0,
        quantity: 1,
        price: 0,
        discount: 0,
    };

    return (
        <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
        >
            {formikProps => (
                <PoolModal open={showGenericItemModal} onClose={handleClose(formikProps)}>
                    <PoolModalTitle>
                        <FormattedMessage
                            id={
                                genericItem
                                    ? `app.purchaseOrders.detail.genericItemEdit.` + genericItemType
                                    : 'app.purchaseOrders.detail.genericItemNew.' + genericItemType
                            }
                        />
                    </PoolModalTitle>
                    <PoolModalContent>
                        <Grid container={true} columnSpacing={2} rowSpacing={0}>
                            <Grid item={true} xs={12}>
                                <PoolFormikTextfield
                                    label={<FormattedMessage id="app.label.title" />}
                                    fullWidth
                                    required={true}
                                    sx={{ mb: 2 }}
                                    name="title"
                                />
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name={'description'}>
                                    {({ field, form }: FieldProps<string>) => (
                                        <PoolMarkdownEditor
                                            value={field.value ?? ''}
                                            onChange={description => form.setFieldValue('description', description)}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </PoolModalContent>
                    <PoolModalActions>
                        <PoolButton variant="text" onClick={handleClose(formikProps)}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>
                        <PoolButton onClick={formikProps.submitForm}>
                            <FormattedMessage id={genericItem ? 'app.common.change' : 'app.common.add'} />
                        </PoolButton>
                    </PoolModalActions>
                </PoolModal>
            )}
        </Formik>
    );
};
